import { UploadFileTypeEnum } from '@dynamika-shared/types/inventory';
import { twJoin } from '@utils/twMerge';
import { Button, ButtonIconType } from '@dynamika-shared/ui/Button/Button';
import { ReactNode, useMemo } from 'react';
import { useQCTaskUploadsListSlice } from '@restore/QCTaskUploadsList/QCTaskUploadsList.slice';
import {
  DxaProcessingStatus,
  QCTaskUpload,
} from '@dynamika-shared/types/qcTaskUpload';
import {
  ExecutionStatus,
  ExecutionType,
} from '@dynamika-shared/types/executions';
import { WithConditionalWrapperHoc } from '@components/HOC/WithConditionalWrapper/WithConditionalWrapper.hoc';
import { Tooltip } from 'antd';
import { useQCTaskSlice } from '@restore/QCTask/QCTask.slice';
import { useAuthSlice } from '@restore/auth/auth.slice';
import { SpinnerThin } from '@dynamika-shared/ui/icons';

type DXAWorkstationProps = {
  upload: QCTaskUpload;
  taskId: string;
  uploadId: string;
  clinicalTrialId: string;
};
type ContentConfigByStatus = {
  button: {
    iconType?: ButtonIconType;
    onClick: () => void;
    buttonName: string;
  } | null;
  badge: ReactNode | null;
};

export const DXAWorkstation = ({
  upload,
  taskId,
  uploadId,
  clinicalTrialId,
}: DXAWorkstationProps) => {
  const [
    sendToWorkstationStatus,
    uploadLoadingId,
    sendToWorkStation,
    fetchQCTaskUpload,
  ] = useQCTaskUploadsListSlice((state, actions) => [
    state.sendToWorkstationStatus,
    state.uploadLoadingId,
    actions.fetchSendToWorkStation,
    actions.fetchQCTaskUpload,
  ]);

  const task = useQCTaskSlice(s => s.data);
  const userData = useAuthSlice(s => s.data);
  const isTaskAssignedToCurrentUser =
    !!task?.assigneeId &&
    task?.assigneeId === userData?.['dynamika7/user_metadata']?.uuid;

  const dxaUploadStatus: DxaProcessingStatus = useMemo(() => {
    if (sendToWorkstationStatus[uploadId]?.sent) {
      return 'IN_PROGRESS';
    }

    const dxaStatuses = (upload?.workflows || [])
      .filter(({ type }) => type === ExecutionType.DXA_UPLOAD)
      .map(({ status }) => status);


    const statusByPriority = [
      ExecutionStatus.COMPLETED,
      ExecutionStatus.IN_PROGRESS,
      ExecutionStatus.CREATED,
      ExecutionStatus.FAILED,
    ];

    return (statusByPriority.find(status => dxaStatuses.includes(status)) ??
      '') as DxaProcessingStatus;
  }, [sendToWorkstationStatus, upload?.workflows, uploadId]);

  if (upload.fileType !== UploadFileTypeEnum.DXA) {
    return null;
  }
  const getContentByStatus: (
    dxaStatus: DxaProcessingStatus
  ) => ContentConfigByStatus = dxaStatus => {
    switch (dxaStatus) {
      case 'IN_PROGRESS':
      case 'CREATED':
        return {
          button: {
            iconType: 'refresh',
            onClick: () =>
              fetchQCTaskUpload({ clinicalTrialId, taskId, uploadId }),
            buttonName: 'Refresh',
          },
          badge: (
            <div className='rounded-[32px] bg-aliceBlue text-honoluluBlue text-1216 px-[12px] py-[4px]'>
              Processing
            </div>
          ),
        };
      case 'COMPLETED': {
        const workstation = upload.verifications
          .find(v => v.status === 'SUCCESS')
          ?.title?.split(' ')
          ?.pop();
        return {
          button: null,
          badge: (
            <div className='flex flex-col items-end gap-1 text-1216'>
              <div className='rounded-[32px] bg-honeydew text-yellowGreen w-fit px-[12px] py-[4px]'>
                Delivered
              </div>
              {workstation && (
                <div className='me-1'>
                  Workstation:{' '}
                  <span className='font-semibold'>
                    {upload.verifications
                      .find(v => v.status === 'SUCCESS')
                      ?.title?.split(' ')
                      ?.pop()}
                  </span>
                </div>
              )}
            </div>
          ),
        };
      }
      case 'FAILED':
        return {
          button: {
            iconType: 'refresh',
            onClick: () =>
              sendToWorkStation({ clinicalTrialId, taskId, uploadId }),
            buttonName: 'Retry',
          },
          badge: (
            <div className='rounded-[32px] bg-seashell text-redOrange text-1216 px-[12px] py-[4px]'>
              Delivery Failed
            </div>
          ),
        };
      default:
        return {
          button: {
            iconType: 'send',
            onClick: () =>
              sendToWorkStation({ clinicalTrialId, taskId, uploadId }),
            buttonName: 'Send to Workstation',
          },
          badge: null,
        };
    }
  };

  const content = getContentByStatus(dxaUploadStatus);

  const disabled =
    !isTaskAssignedToCurrentUser ||
    upload.status === 'FAILED' ||
    uploadLoadingId === uploadId;

  return (
    <div className='flex gap-1 items-center'>
      <WithConditionalWrapperHoc
        useWrapper={disabled}
        wrapper={
          <Tooltip
            color='white'
            title={
              <span className='text-1424 text-black'>
                {!isTaskAssignedToCurrentUser
                  ? 'Only task assignee can perform this action'
                  : 'Upload data is corrupted'}
              </span>
            }
          />
        }
      >
        {uploadLoadingId === uploadId ? (
          <SpinnerThin className='h-3 w-3 text-vividTangelo' />
        ) : (
          <>
            <Button
              iconType={content?.button?.iconType ?? undefined}
              type='text'
              className={twJoin(
                'flex items-center !font-medium without-bg -ml-1 text-1216 !p-0',
                disabled && 'opacity-30 !text-black !:hover:text-black'
              )}
              onClick={content?.button?.onClick}
              disabled={disabled}
            >
              {content?.button?.buttonName}
            </Button>
            {content?.badge ?? null}
          </>
        )}
      </WithConditionalWrapperHoc>
    </div>
  );
};
